<script setup>
import { ref, computed } from 'vue'
import Dropdown from '@/components/Dropdown.vue'
import AShirt from '@/components/AShirt.vue'
import HShirt from '@/components/HShirt.vue'
import http from '@/composables/axios'

const assetsUrl = import.meta.env.VITE_ASSETS_URL

const props = defineProps({
  event: { type: Object, required: true },
  isLive: { type: Boolean, default: false }
})

const loading = ref(false)
const stats = ref(null)
const homeKitError = ref(false)
const awayKitError = ref(false)

const fetchStats = () => {
  if (!props.event?.i) return
  loading.value = true
  stats.value = null

  return http
    .post('07/fixtures/scoreboard', { events: [props.event.i] })
    .then(res => {
      if (res && res.data) stats.value = res.data[0] || res.data
    })
    .catch(err => console.error('Error loading stats:', err))
    .finally(() => (loading.value = false))
}

const sport1Incidents = computed(() => {
  if (!stats.value?.inf) return []
  const inf = stats.value.inf
  return [
    { key: 'AT', icon: '/incidents/attacks.svg' },
    { key: 'DA', icon: '/incidents/dangerous.svg' },
    { key: 'POSSESSION', icon: '/incidents/possession.svg' },
    { key: 'SHOFT', icon: '/incidents/shoft.svg' },
    { key: 'SC', icon: '/incidents/sc.svg' },
    { key: 'COR', icon: '/incidents/corner.svg' },
    { key: 'YC', icon: '/incidents/yellow_card.svg' },
    { key: 'RC', icon: '/incidents/red_card.svg' },
  ].map(item => ({
    ...item,
    home: inf[item.key]?.split('-')[0] ?? '0',
    away: inf[item.key]?.split('-')[1] ?? '0'
  }))
})
</script>

<template>
  <div class="relative" @click.stop>
    <Dropdown width="56" :align="'top'">
      <template #trigger>
        <button
          v-if="isLive"
          class="ev-StatIconTrigger block"
          @click="fetchStats"
        >
          <div class="ev-StatIcon" />
        </button>
      </template>

      <template #content>
        <div class="p-1 text-[10px] bg-white rounded shadow-md text-gray-900 w-56">
          <div v-if="loading" class="text-gray-500 text-center py-1">
            Loading...
          </div>

          <div v-else-if="stats" class="w-full">
            <!-- Header icons -->
            <div class="grid grid-cols-[auto_repeat(8,1fr)] items-center text-center gap-x-1 pb-[2px] border-b border-gray-200">
              <div class="w-3"></div>
              <template v-for="incident in sport1Incidents" :key="incident.key">
                <img :src="incident.icon" class="w-5 h-5 mx-auto" />
              </template>
            </div>

            <!-- Home row -->
            <div class="grid grid-cols-[auto_repeat(8,1fr)] items-center text-center py-[1px]">
              <div class="flex justify-center">
                <img
                  v-if="!homeKitError && props.event?.hi"
                  :src="`${assetsUrl}/teams/${props.event.hi}.png`"
                  class="w-3.5 h-3.5"
                  @error="homeKitError = true"
                />
                <HShirt v-else class="w-3.5 h-3.5" />
              </div>
              <template v-for="incident in sport1Incidents" :key="'home-' + incident.key">
                <span class="font-medium">{{ incident.home }}</span>
              </template>
            </div>

            <!-- Away row -->
            <div class="grid grid-cols-[auto_repeat(8,1fr)] items-center text-center py-[1px]">
              <div class="flex justify-center">
                <img
                  v-if="!awayKitError && props.event?.ai"
                  :src="`${assetsUrl}/teams/${props.event.ai}.png`"
                  class="w-3.5 h-3.5"
                  @error="awayKitError = true"
                />
                <AShirt v-else class="w-3.5 h-3.5" />
              </div>
              <template v-for="incident in sport1Incidents" :key="'away-' + incident.key">
                <span class="font-medium">{{ incident.away }}</span>
              </template>
            </div>
          </div>

          <div v-else class="text-gray-400 text-center py-1">
            No stats
          </div>
        </div>
      </template>
    </Dropdown>
  </div>
</template>

<style scoped>
.ev-StatIcon {
  mask-image: url('/stat-icon.svg');
  background-color: var(--color-grey6);
  mask-size: 11px 11px;
  width: 11px;
  height: 11px;
}
</style>
